import { blockedStatus, enStatus, ILesson, IModule } from 'interface/course';
import defaultImage from 'assets/img/default-lesson.png';
import { dateFormater, getCdnUrl } from 'utils';
import { Box, Button, makeStyles, Typography } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { CourseTheme } from 'assets/themes/Course';
import courseService from 'services/course';
import { useAppSelector } from 'hooks/redux';

const useStyles = makeStyles<CourseTheme>((theme) => ({
  container: {
    '@media(max-width: 768px)': {
      '& h4': {
        fontSize: 14,
        margin: theme.spacing(3, 0),
      },
      '& h3': {
        fontSize: 16,
      },
      '& img': {
        display: 'none',
      },
      '& > div > div': {
        justifyContent: 'flex-start',
        height: 'fit-content',
        maxHeight: 180,
      },
    },
    marginBottom: theme.spacing(6),
    '& h3, h4, p': {
      color: theme.variables.courseTheme.text,
    },
  },
  kepWatchingContainer: {
    background: theme.variables.courseTheme.cardBackground,
    padding: theme.spacing(3, 7, 3, 7),
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'row',
    '& img': {
      minWidth: 160,
      maxWidth: 160,
      height: 120,
      borderRadius: 8,
      objectFit: 'cover',
      margin: theme.spacing(4, 4, 0, 0),
    },
    '& button': {
      fontSize: 14,
      fontWeight: 600,
      textTransform: 'capitalize',
      marginBottom: theme.spacing(2),
    },
  },
  lessonTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 144,
    justifyContent: 'space-between',
    padding: theme.spacing(2, 0),
    overflow: 'hidden',
    '& button': {
      width: 'fit-content',
    },
    '& h3': {
      marginTop: theme.spacing(2),
    },
  },
}));

const KeepWatching = () => {
  const [lesson, setLesson] = useState<ILesson>(null);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { courseHash } = useParams<{ courseHash: string }>();
  const modules: IModule[] = useAppSelector((state) => state.newModules.modules);
  const courseStatus = useAppSelector((state) => state.course.status);
  const classes = useStyles();
  const history = useHistory();

  const goToLesson = () => {
    history.push(`${courseHash}/aula/${lesson.id}`);
  };

  const canShowKeepWatching = useMemo(() => {
    const allowedStatus = [
      enStatus.LEARNER,
      enStatus.PRODUCER,
      enStatus.FREE,
      enStatus.MUST_ACCEPT_TERM,
      enStatus.NOT_RELEASED,
    ];
    return allowedStatus.includes(courseStatus) && !!modules && !!courseHash;
  }, [courseStatus, modules, courseHash]);

  useEffect(() => {
    if (canShowKeepWatching) {
      const lessons = modules.flatMap((module) => module.lessons);
      courseService
        .getKeepWatching(courseHash)
        .then((nextLesson) => lessons.find((lesson) => lesson?.id === nextLesson?.id))
        .then(setLesson);
    }
  }, [courseHash, modules, canShowKeepWatching]);

  if (!lesson || !isAuthenticated) {
    return null;
  }

  return (
    <div className={classes.container}>
      <Box marginBottom={4}>
        <Typography variant='h3'>Continuar aula</Typography>
      </Box>
      <div className={classes.kepWatchingContainer}>
        <img alt='capa da aula' src={getCdnUrl(lesson.image) || defaultImage} />
        <div className={classes.lessonTextContainer}>
          <Typography aria-label='título da aula' variant='h3' noWrap>
            {lesson.title}
          </Typography>
          <Typography aria-label='Descrição curta da aula' variant='h4' noWrap>
            {lesson.short_description}
          </Typography>
          {lesson.status === enStatus.NOT_RELEASED && lesson.release && (
            <Typography aria-label='Status da aula' variant='body1'>
              Essa aula será liberada no dia {dateFormater(lesson.release)}
            </Typography>
          )}
          {lesson.status === enStatus.NOT_RELEASED && !lesson.release && (
            <Typography aria-label='Status da aula' variant='body1'>
              Essa aula está bloqueada, aguarde a liberação
            </Typography>
          )}
          {lesson.status !== enStatus.NOT_RELEASED && (
            <Button
              aria-label='Botão iniciar aula'
              disabled={blockedStatus.includes(lesson.status)}
              color='primary'
              variant='contained'
              onClick={goToLesson}
            >
              {blockedStatus.includes(lesson.status) ? 'Aula bloqueada' : 'Continuar aula'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default KeepWatching;
